/**
 * Qavatlarning NOMINAL o'rni — qator bo'yicha bitta narvon.
 *
 * Muammo: reyestrda har bir stellaj olti qavatli emas. A01
 * qatorining 49 ta stellajida faqat 5- va 6-qavat bor (pastida
 * o'tish joyi), 63 tasida esa 1-qavat butun bo'yli pallet o'rni
 * va 2-3 qavat umuman yo'q.
 *
 * Chizma qavatlarni HAR BIR STELLAJNING o'zidan hisoblaganda
 * shunday stellajda 5-qavat polga tushib qolardi — yonidagi
 * stellajning 1-qavati bilan bir chiziqda. Xodim chizmaga
 * qarab «bu past javon» deb o'ylardi, aslida u boshi uzra.
 *
 * Shuning uchun qavat o'rni butun QATOR bo'yicha bir marta
 * hisoblanadi: 5-qavat hamma stellajda bir balandlikda, pastida
 * nimasi borligidan qat'i nazar. Stellajda qavat bo'lmasa —
 * o'rni bo'sh qoladi, pastdagisi ko'tarilmaydi.
 */

export interface LadderCell {
  rack: number;
  level: number;
  /** Katakning reyestrdagi balandligi, sm. */
  heightCm: number;
}

export interface LevelSlot {
  level: number;
  /** Poldan qavat tagigacha, sm. */
  bottomCm: number;
  /** Qavatning nominal balandligi, sm. */
  heightCm: number;
}

export interface Ladder {
  /** Pastdan yuqoriga: 1-qavat birinchi. */
  slots: LevelSlot[];
  /** Narvonning to'liq balandligi, sm — hamma stellaj uchun bir xil. */
  totalCm: number;
  /** Qavat tagining poldan balandligi, sm. */
  bottomOf: (level: number) => number;
  /**
   * Stellajdagi qavatning CHIZILADIGAN balandligi, sm.
   * Qavat bu stellajda bo'lmasa — 0.
   */
  spanOf: (rack: number, level: number) => number;
}

const EMPTY: Ladder = {
  slots: [],
  totalCm: 0,
  bottomOf: () => 0,
  spanOf: () => 0,
};

export function buildLadder(cells: LadderCell[]): Ladder {
  if (cells.length === 0) return EMPTY;

  // Qavat balandligi — o'sha qavatda ENG KO'P uchraydigan
  // balandlik, eng balandi emas. A01 da 1-qavatning 1 720 tasi
  // 75 sm, 204 tasi esa 190 sm (pastki uch javon o'rniga butun
  // bo'yli pallet). Eng balandini olsak, oddiy stellajning
  // 2-qavati 115 sm bo'sh joydan keyin boshlanardi.
  const seen = new Map<number, Map<number, number>>();
  const rackLevel = new Map<number, Map<number, number>>();

  for (const cell of cells) {
    const byHeight = seen.get(cell.level) ?? new Map<number, number>();
    byHeight.set(cell.heightCm, (byHeight.get(cell.heightCm) ?? 0) + 1);
    seen.set(cell.level, byHeight);

    const byLevel = rackLevel.get(cell.rack) ?? new Map<number, number>();
    byLevel.set(cell.level, Math.max(byLevel.get(cell.level) ?? 0, cell.heightCm));
    rackLevel.set(cell.rack, byLevel);
  }

  const nominal = new Map<number, number>();
  for (const [level, byHeight] of seen) {
    let best = 0;
    let bestCount = 0;
    for (const [height, count] of byHeight) {
      // Teng uchraganda balandrog'i: past javon ostida qolgan
      // katak ko'rinmay qolishidan ko'ra ortiqcha tirqish yaxshi.
      if (count > bestCount || (count === bestCount && height > best)) {
        best = height;
        bestCount = count;
      }
    }
    nominal.set(level, best);
  }

  const present = [...nominal.keys()].sort((a, b) => a - b);
  const lowest = present[0]!;
  const highest = present[present.length - 1]!;
  // Oraliqdagi qavat ko'rinishda bo'lmasa ham o'rni qoladi:
  // 4-qavat 3-qavatning o'rniga sirg'alib tushmasligi kerak.
  const fallback = median([...nominal.values()]);

  const slots: LevelSlot[] = [];
  let bottom = 0;
  for (let level = lowest; level <= highest; level++) {
    const height = nominal.get(level) ?? fallback;
    slots.push({ level, bottomCm: bottom, heightCm: height });
    bottom += height;
  }

  const byLevel = new Map(slots.map((slot) => [slot.level, slot]));
  const bottomOf = (level: number) => byLevel.get(level)?.bottomCm ?? 0;

  return {
    slots,
    totalCm: bottom,
    bottomOf,
    spanOf(rack, level) {
      const own = rackLevel.get(rack)?.get(level);
      if (own == null || own <= 0) return 0;

      // Bu stellajdagi KEYINGI qavatgacha — undan nariga
      // o'tmaydi. Butun bo'yli pallet (190 sm) 1-qavatda
      // turganda u 2-3 javonlarning bo'sh o'rnini egallaydi;
      // ustidagi 4-qavatga esa kirib ketmaydi.
      let ceiling = Infinity;
      for (const slot of slots) {
        if (slot.level > level && (rackLevel.get(rack)?.get(slot.level) ?? 0) > 0) {
          ceiling = slot.bottomCm - bottomOf(level);
          break;
        }
      }
      return Math.min(own, ceiling);
    },
  };
}

function median(values: number[]): number {
  if (values.length === 0) return 100;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)]!;
}
